import clsx from 'clsx'
import { useRouter } from 'next/router'
import type React from 'react'
import { useCallback, useState, type FunctionComponent, type ReactNode } from 'react'
import slugify from 'slugify'
import { SimpleSwiper } from '../swiper/SimpleSwiper'
import { Hidden } from './Hidden'
import { TabButtons } from './TabButtons'
import style from './Tabs.module.sass'
import { ToggleTabs } from './ToggleTabs'

type TabsProps = {
	tabItems: {
		content: React.ReactNode
		contentMobile?: React.ReactNode
		title: string
		disabled?: boolean
	}[]
	isProductLocated?: boolean
	isSearchLocated?: boolean
	title?: JSX.Element | string
}

export const Tabs: FunctionComponent<TabsProps> = ({
	tabItems,
	title,
	isProductLocated = false,
	isSearchLocated = false,
}) => {
	const router = useRouter()
	const { query } = useRouter()

	const titleWithoutNumber = (title: string) => {
		return title.split('-(')[0]
	}

	const firstTab = useCallback(
		(tabItems: { content: ReactNode; title: string; disabled?: boolean }[]) => {
			const tabInQuery = tabItems.find((tab) => query.tab === titleWithoutNumber(slugify(tab.title)))
			const tabIsDisabled = tabItems.some((tab) => tab.disabled)

			if (tabInQuery && !tabInQuery.disabled) {
				return titleWithoutNumber(slugify(tabInQuery.title))
			}
			if (tabIsDisabled) {
				const firstNotDisabledTab = tabItems.find((tab) => !tab.disabled)
				return firstNotDisabledTab?.title ? titleWithoutNumber(slugify(firstNotDisabledTab.title)) : undefined
			}
		},
		[query.tab],
	)

	const [activeTabId, setActiveTabId] = useState(firstTab(tabItems) || titleWithoutNumber(slugify(tabItems[0].title)))

	const changeTab = useCallback(
		(title: string) => {
			const titleSlug = slugify(title.split('-(')[0])
			setActiveTabId(titleSlug)
			router.replace({ pathname: router.pathname, query: { ...query, tab: titleSlug } }, undefined, {
				scroll: false,
			})
		},
		[query, router],
	)

	return (
		<div className={clsx(style.root, title && style.rootWithTitle)}>
			<Hidden breakpoint="mobileDown">
				<div className={title ? style.content : ''}>
					{title && <div className={style.titleSearch}>{title}</div>}
					<div className={clsx(style.buttonGroup, title && style.buttonGroupNoMargin)}>
						{tabItems.map((item) => (
							<span key={item.title}>
								<TabButtons
									disabled={item.disabled}
									activeId={activeTabId}
									label={item.title}
									id={titleWithoutNumber(slugify(item.title))}
									changeTab={() => changeTab(slugify(item.title))}
									isTitle={isSearchLocated}
								/>
							</span>
						))}
					</div>
				</div>
				{tabItems.map((item) => (
					<span key={item.title}>
						{activeTabId === titleWithoutNumber(slugify(item.title)) && (
							<div key={item.title} className={style.tabContent}>
								{item.content}
							</div>
						)}
					</span>
				))}
			</Hidden>
			<Hidden breakpoint="mobileUp">
				{isProductLocated ? (
					tabItems.map((item) => (
						<ToggleTabs
							key={item.title}
							item={item}
							onClick={() => changeTab(item.title)}
							isTabOpen={activeTabId === titleWithoutNumber(slugify(item.title))}
						/>
					))
				) : (
					<>
						<div className={style.buttonGroup}>
							<SimpleSwiper isTransparent>
								{tabItems.map((item) => (
									<span key={item.title} className={style.titleSwiper}>
										<TabButtons
											disabled={item.disabled}
											activeId={activeTabId}
											label={item.title}
											id={titleWithoutNumber(slugify(item.title))}
											changeTab={() => changeTab(slugify(item.title))}
											isTitle={isSearchLocated}
										/>
									</span>
								))}
							</SimpleSwiper>
						</div>
						<div>
							{tabItems.map((item) => (
								<span key={item.title}>
									{activeTabId === titleWithoutNumber(slugify(item.title)) && (
										<div key={item.title} className={style.tabContent}>
											{item.contentMobile ?? item.content}
										</div>
									)}
								</span>
							))}
						</div>
					</>
				)}
			</Hidden>
		</div>
	)
}
